import React from 'react'

import { Dropdown, useResponsiveDropdown } from '@sketch/components'
import ProjectDropdown, {
  ProjectArchivedDropdown,
  ProjectDropdownProps,
  ProjectTrashedDropdown,
} from '../../../projects/components/ProjectDropdown'
import {
  DropdownButton,
  DropdownButtonEllipsis,
} from '../../../shares/components/DocumentItem/DocumentItem.styles'
import { NestedProjectHeaderActionsProps } from './types'
import { useFolderTerm } from '@sketch/modules-common'
import { ProjectFragment, useGetProjectInSidebarQuery } from '@sketch/gql-types'

interface ProjectDropdownLoaderProps
  extends Omit<ProjectDropdownProps, 'project'> {
  project: Pick<ProjectFragment, 'identifier'>
}

const ProjectDropdownLoader = (props: ProjectDropdownLoaderProps) => {
  const { project, ...rest } = props

  const { data, loading, error } = useGetProjectInSidebarQuery({
    variables: { shortId: project.identifier },
  })

  if (loading) {
    return (
      <>
        <Dropdown.Skeleton />
        <Dropdown.Skeleton />
        <Dropdown.Skeleton />
      </>
    )
  }

  if (error || !data?.project) {
    return null
  }

  return <ProjectDropdown {...rest} project={data?.project} />
}

const ProjectDropdownPicker = (props: NestedProjectHeaderActionsProps) => {
  const { nestedProject: project, workspace } = props

  // we have enough information to render the dropdown for archived or trashed projects
  // so we can skip the loader
  if (project.archivedAt) {
    return <ProjectArchivedDropdown workspace={workspace} project={project} />
  }
  if (project.deletedAt) {
    return <ProjectTrashedDropdown workspace={workspace} project={project} />
  }

  return <ProjectDropdownLoader workspace={workspace} project={project} />
}

/**
 * Dropdown for nested project actions that appears in the project view, with nested project as thumbnails
 */
const NestedProjectDropdown: React.FC<
  NestedProjectHeaderActionsProps
> = props => {
  const folderTerm = useFolderTerm()

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: ProjectDropdownPicker,
    dropdownProps: props,
    placement: 'bottom-end',
    usePortal: true,
  })

  return (
    <div onClick={event => event.stopPropagation()}>
      <DropdownButton data-testid="nestedProject-dropdown" {...buttonProps}>
        <DropdownButtonEllipsis aria-label={`${folderTerm.upper} Settings`} />
        <span className="sr-only">{folderTerm.upper} Settings</span>
      </DropdownButton>
      {content}
    </div>
  )
}

export default NestedProjectDropdown
